'use client'
import { useEffect, useState, useCallback } from 'react'
import { useIstApp } from '@/components/layout/useIstApp'

type Scope = 'djsuche' | 'website'
interface Script   { id: string; label: string; category: string; code: string }
interface Category { key: string; label: string; description: string }
interface Config {
  enabled: boolean; title: string; message: string; privacyUrl: string; imprintUrl: string
  acceptAllLabel: string; rejectAllLabel: string; settingsLabel: string; saveLabel: string
  accentColor: string; necessaryNote: string; categories: Category[]; scripts: Script[]
}

const CONSENT_VERSION = 'v1'
const injected = new Set<string>()

function detectScope(): Scope {
  const h = location.hostname
  if (h === 'djsuche.eu' || h === 'www.djsuche.eu') return 'djsuche'
  const p = location.pathname
  if (p === '/djsuche' || p.startsWith('/djsuche/')) return 'djsuche'
  return 'website'
}

function consentKey(scope: Scope) { return `cookie-consent:${scope}:${CONSENT_VERSION}` }

function readConsent(scope: Scope): Record<string, boolean> | null {
  try {
    const raw = localStorage.getItem(consentKey(scope))
    if (!raw) return null
    const o = JSON.parse(raw)
    return (o && typeof o.categories === 'object') ? o.categories : null
  } catch { return null }
}

function writeConsent(scope: Scope, categories: Record<string, boolean>) {
  try { localStorage.setItem(consentKey(scope), JSON.stringify({ categories, ts: new Date().toISOString() })) } catch {}
}

// Injiziert ein rohes HTML/Script-Snippet so, dass enthaltene <script> wirklich ausgeführt werden.
function injectSnippet(id: string, code: string) {
  if (injected.has(id) || !code.trim()) return
  injected.add(id)
  const tpl = document.createElement('template')
  tpl.innerHTML = code
  tpl.content.childNodes.forEach((node) => {
    if (node.nodeName === 'SCRIPT') {
      const old = node as HTMLScriptElement
      const sc = document.createElement('script')
      for (const attr of Array.from(old.attributes)) sc.setAttribute(attr.name, attr.value)
      sc.text = old.textContent ?? ''
      document.head.appendChild(sc)
    } else if (node.nodeType === 1 || node.nodeType === 3) {
      document.body.appendChild(node.cloneNode(true))
    }
  })
}

function applyConsent(cfg: Config, accepted: Record<string, boolean>) {
  for (const s of cfg.scripts) {
    if (s.category === 'necessary' || accepted[s.category]) injectSnippet(s.id, s.code)
  }
}

export default function CookieBanner() {
  const inApp = useIstApp()
  const [scope, setScope]   = useState<Scope | null>(null)
  const [cfg, setCfg]       = useState<Config | null>(null)
  const [open, setOpen]     = useState(false)        // Banner sichtbar
  const [showPrefs, setPrefs] = useState(false)      // Detail-Einstellungen
  const [sel, setSel]       = useState<Record<string, boolean>>({})
  const [reopen, setReopen] = useState(false)        // kleiner Wiederöffnen-Button

  useEffect(() => {
    const sc = detectScope()
    setScope(sc)
    fetch(`/api/public/cookie-banner?scope=${sc}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: { config: Config } | null) => {
        if (!d?.config) return
        const c = d.config
        setCfg(c)
        if (!c.enabled) return
        const prior = readConsent(sc)
        if (prior) { applyConsent(c, prior); setReopen(true) }
        else { setSel(Object.fromEntries(c.categories.map(cat => [cat.key, false]))); setOpen(true) }
      })
      .catch(() => {})
  }, [])

  const decide = useCallback((accepted: Record<string, boolean>) => {
    if (!cfg || !scope) return
    writeConsent(scope, accepted)
    applyConsent(cfg, accepted)
    setOpen(false); setPrefs(false); setReopen(true)
  }, [cfg, scope])

  if (!cfg || !cfg.enabled) return null

  const accent = cfg.accentColor || '#1e293b'

  if (!open) {
    // Dezenter Wiederöffnen-Button nach getroffener Entscheidung
    return reopen ? (
      <button
        onClick={() => { setSel(readConsent(scope!) ?? Object.fromEntries(cfg.categories.map(c => [c.key, false]))); setOpen(true) }}
        aria-label="Cookie-Einstellungen"
        className="fixed left-3 z-[9998] w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-lg hover:scale-105 transition-transform"
        // wie oben: in der App ueber der Tab-Leiste, sonst ueber dem
        // Home-Indicator. Ohne das laege der Knopf auf dem Tab "Community".
        style={{
          bottom: inApp
            ? 'calc(4.75rem + env(safe-area-inset-bottom))'
            : 'calc(0.75rem + env(safe-area-inset-bottom))',
        }}
        title="Cookie-Einstellungen"
      >🍪</button>
    ) : null
  }

  return (
    <div
      className="fixed inset-x-0 z-[9999] p-3 sm:p-4"
      // Der Banner haengt im Wurzel-Layout und erbt die Variablen aus
      // app/(hub)/layout.tsx nicht, deshalb hier die eigene Rechnung: in der
      // App ueber der unteren Tab-Leiste, sonst mit Abstand zum
      // Home-Indicator (seit viewport-fit='cover' noetig).
      style={inApp
        ? { bottom: 'calc(4rem + env(safe-area-inset-bottom))' }
        : { bottom: 0, paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
        <div className="p-5 sm:p-6">
          <h2 className="text-base sm:text-lg font-black text-gray-900">{cfg.title}</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">{cfg.message}</p>

          <p className="mt-2 text-xs text-gray-400">
            {cfg.privacyUrl && <a href={cfg.privacyUrl} target="_blank" rel="noopener" className="underline hover:text-gray-600">Datenschutz</a>}
            {cfg.privacyUrl && cfg.imprintUrl && ' · '}
            {cfg.imprintUrl && <a href={cfg.imprintUrl} target="_blank" rel="noopener" className="underline hover:text-gray-600">Impressum</a>}
          </p>

          {showPrefs && (
            <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
              <div className="flex items-start gap-3 opacity-70">
                <input type="checkbox" checked disabled className="mt-1" />
                <div><p className="text-sm font-bold text-gray-800">Notwendig</p><p className="text-xs text-gray-500">{cfg.necessaryNote}</p></div>
              </div>
              {cfg.categories.map(cat => (
                <label key={cat.key} className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!sel[cat.key]} onChange={e => setSel(s => ({ ...s, [cat.key]: e.target.checked }))} className="mt-1" />
                  <div><p className="text-sm font-bold text-gray-800">{cat.label}</p><p className="text-xs text-gray-500">{cat.description}</p></div>
                </label>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            {showPrefs ? (
              <button onClick={() => decide(sel)} style={{ backgroundColor: accent }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-black text-white hover:opacity-90 transition">{cfg.saveLabel}</button>
            ) : (
              <button onClick={() => decide(Object.fromEntries(cfg.categories.map(c => [c.key, true])))} style={{ backgroundColor: accent }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-black text-white hover:opacity-90 transition">{cfg.acceptAllLabel}</button>
            )}
            <button onClick={() => decide(Object.fromEntries(cfg.categories.map(c => [c.key, false])))}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition">{cfg.rejectAllLabel}</button>
            {!showPrefs && (
              <button onClick={() => setPrefs(true)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold border border-gray-300 text-gray-700 hover:bg-gray-50 transition">{cfg.settingsLabel}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
